import type {
  CandidateDetailsInterviewProps,
  CandidateDetailsProcedureGroup,
  CandidateDetailsResume,
} from '@tempmee/react-blocks';
import {
  toProcedureGroups,
  toSpecialtyChips,
} from '~/components/ShiftDetailsRedesign/shared/utils/candidateProfileProps';
import type { ICandidateResumeEntity } from '~/domain/PermanentJobs/types/CandidateResume';
import { UserSubProfession } from '~/enums/UserProfession';
import type { IUser } from '~/models';
import { toShiftCount } from '~/utils/candidateBadges';
import { toExperienceLabel } from '~/utils/professional';
import { formatExperienceDates, formatGraduationDate } from './Resume';

export interface IInterviewCandidateDetailsSource {
  professional: IUser | null | undefined;
  /**
   * The posting's profession code, e.g. `RDH`: it picks which of the candidate's
   * specialties and procedures count. Omitted, the candidate's own licence decides.
   */
  profession?: string;
  resume: ICandidateResumeEntity | null | undefined;
}

/** The Interview block props this adapter fills — the tab state stays with the caller. */
export type TInterviewCandidateDetailsProps = Omit<
  CandidateDetailsInterviewProps,
  'tab' | 'defaultTab' | 'onTabChange'
>;

/**
 * A resume worth a tab: a summary or at least one job. Anything less and the
 * block opens on Experience & Skills with no switcher. The same rule as the
 * mobile app's Candidate Details page.
 */
export const hasCandidateResume = (
  resume: ICandidateResumeEntity | null | undefined,
): resume is ICandidateResumeEntity =>
  resume != null && (!!resume.summary || (resume.experiences?.length ?? 0) > 0);

const toResume = (
  resume: ICandidateResumeEntity,
  professional: IUser | null | undefined,
): CandidateDetailsResume => ({
  // The block renders "About {name}"; never let a missing name print "About undefined".
  name: resume.professionalFirstName || professional?.first_name || '',
  summary: resume.summary || undefined,
  experiences: (resume.experiences ?? []).map((experience) => ({
    employer: experience.employerName,
    title: experience.title || undefined,
    dates: formatExperienceDates(
      experience.startDate,
      experience.endDate,
      experience.isCurrentEmployer,
    ),
  })),
  // The block prints "Graduation Date:" itself, so this is the date alone. An
  // entry with neither a school nor a date would only draw an empty row.
  education: (resume.education ?? [])
    .filter((education) => education.institutionName || education.graduationDate)
    .map((education) => ({
      institution: education.institutionName || undefined,
      graduation: education.graduationDate
        ? formatGraduationDate(education.graduationDate)
        : undefined,
    })),
});

/**
 * The Interview Figma lists a specialty with no procedures under it as a bare
 * bold label, two to a row, above the groups: the block's `certifications`.
 * Left as a group it would read "Coronal Polishing 0". Same split as mobile.
 */
export const toProceduresAndCertifications = (
  groups: readonly CandidateDetailsProcedureGroup[],
): Pick<TInterviewCandidateDetailsProps, 'procedures' | 'certifications'> => ({
  certifications: groups
    .filter((group) => (group.items?.length ?? 0) === 0)
    .map((group) => group.label),
  procedures: groups.filter((group) => (group.items?.length ?? 0) > 0),
});

/**
 * The Interview `CandidateDetails` props (GOTU-8148) for the perm Candidate
 * Details page. The Experience & Skills half reuses the Shift Details mappers,
 * so a candidate reads the same on both pages; badges stay off, as the
 * Interview Figma draws none.
 */
export const toInterviewCandidateDetailsProps = ({
  professional,
  profession,
  resume,
}: IInterviewCandidateDetailsSource): TInterviewCandidateDetailsProps => {
  // The mappers read only the profession off the job, and file an EFDA's
  // education under DA: the sub-profession code would find nothing.
  const educationProfession =
    (profession && UserSubProfession.getProfession(profession)) || profession;
  const job = educationProfession ? { profession: educationProfession } : null;

  return {
    detailsType: 'interview',
    resume: hasCandidateResume(resume) ? toResume(resume, professional) : undefined,
    shiftCount: toShiftCount(professional),
    experience: toExperienceLabel(professional),
    rating: professional?.average_rating ?? 0,
    specialties: toSpecialtyChips(professional, job),
    ...toProceduresAndCertifications(
      toProcedureGroups(professional, job, { flatLabel: 'Procedures', keepEmptyGroups: true }),
    ),
  };
};
